// Certificados de aptitud médica de un trabajador (Anexo 1 PO 4): sus exámenes con el estado del registro firmado.
// Cada certificado se firma con RegistroFirmasModal: el trabajador firma en pantalla y se sube el certificado firmado
// por el médico (o el formato impreso firmado por los dos).

import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { fechaEc } from '../../lib/fechas';
import RegistroFirmasModal from './RegistroFirmasModal';
import { cargarFirmas, contenidoDeExamen, type DatosFirmas } from '../../lib/firmas/firmas-datos';
import { estadoVisible, faltantesContenido, type ExamenOrigen } from '../../lib/firmas/firmas-logica';
import { ETIQUETA_APTITUD, ETIQUETA_TIPO_EXAMEN } from '../../lib/firmas/firmas-tipos';

interface Props {
  companyId: string;
  empresa: string;
  trabajador: { id: string; full_name: string };
  usuarioId: string | null;
  puedeEditar: boolean;
  esAdmin: boolean;
  onCerrar: () => void;
}

// exam_date es una fecha sin hora: se muestra tal cual (lib/fechas).
const fecha = (iso: string) => fechaEc(iso);

export default function CertificadosAptitudModal(p: Props) {
  const [examenes, setExamenes] = useState<ExamenOrigen[]>([]);
  const [cedula, setCedula] = useState<string | null>(null);
  const [datos, setDatos] = useState<DatosFirmas>({ registros: [], firmas: [], itemsEpp: [] });
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [abierto, setAbierto] = useState<ExamenOrigen | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true); setError('');
    try {
      const [{ data: ex, error: e1 }, { data: w, error: e2 }, d] = await Promise.all([
        supabase.from('worker_medical_exams').select('id, worker_id, exam_date, exam_type, aptitude, physician')
          .eq('company_id', p.companyId).eq('worker_id', p.trabajador.id).order('exam_date', { ascending: false }),
        supabase.from('workers').select('cedula').eq('id', p.trabajador.id).maybeSingle(),
        cargarFirmas(p.companyId, 'aptitud_medica'),
      ]);
      if (e1 || e2) throw new Error('No se pudieron leer los exámenes del trabajador.');
      setExamenes((ex ?? []) as ExamenOrigen[]);
      setCedula((w as { cedula: string | null } | null)?.cedula ?? null);
      setDatos(d);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setCargando(false);
    }
  }, [p.companyId, p.trabajador.id]);
  useEffect(() => { cargar(); }, [cargar]);

  const registroDe = (examenId: string) => datos.registros.find(r => r.medical_exam_id === examenId) ?? null;
  const firmasDe = (id: string | undefined) => (id ? datos.firmas.filter(f => f.record_id === id) : []);

  if (abierto) {
    const reg = registroDe(abierto.id);
    return (
      <RegistroFirmasModal
        titulo={`${p.trabajador.full_name} · ${ETIQUETA_TIPO_EXAMEN[abierto.exam_type ?? ''] ?? 'Examen'} del ${fecha(abierto.exam_date)}`}
        companyId={p.companyId}
        usuarioId={p.usuarioId}
        registro={reg}
        firmas={firmasDe(reg?.id)}
        origen={{ medical_exam_id: abierto.id, worker_id: abierto.worker_id }}
        armarContenido={async () => {
          const contenido = contenidoDeExamen(abierto, { full_name: p.trabajador.full_name, cedula }, p.empresa);
          return { contenido, faltantes: faltantesContenido(contenido) };
        }}
        puedeEditar={p.puedeEditar}
        esAdmin={p.esAdmin}
        onCambio={cargar}
        onCerrar={() => setAbierto(null)}
      />
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '12px 8px', overflowY: 'auto' }}
      onClick={e => e.target === e.currentTarget && p.onCerrar()} role="dialog" aria-modal="true" aria-label="Certificados de aptitud médica">
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 12, width: '100%', maxWidth: 560, boxSizing: 'border-box', margin: 'auto 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, padding: '16px 16px 10px', borderBottom: '1px solid #334155' }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ color: '#f1f5f9', fontSize: '1rem', fontWeight: 700, margin: 0 }}>Certificados de aptitud médica</h2>
            <p style={{ color: '#94a3b8', fontSize: '0.8rem', margin: '4px 0 0', overflowWrap: 'anywhere' }}>{p.trabajador.full_name}</p>
          </div>
          <button type="button" onClick={p.onCerrar} aria-label="Cerrar" style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: '1.2rem', cursor: 'pointer', minWidth: 40, minHeight: 40 }}>✕</button>
        </div>
        <div style={{ padding: '12px 16px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p style={{ color: '#94a3b8', fontSize: '0.78rem', margin: 0 }}>
            Firma del trabajador en pantalla y certificado firmado por el médico. Los exámenes se registran en Trabajadores.
          </p>
          {error && <p role="alert" style={{ color: '#f87171', fontSize: '0.8rem', margin: 0 }}>{error}</p>}
          {cargando ? <p style={{ color: '#64748b', fontSize: '0.85rem' }}>Cargando…</p>
            : examenes.length === 0 ? <p style={{ color: '#64748b', fontSize: '0.85rem' }}>Este trabajador no tiene exámenes registrados.</p>
              : examenes.map(ex => {
                const reg = registroDe(ex.id);
                const est = estadoVisible(reg, firmasDe(reg?.id));
                const color = est.estado === 'firmado' ? '#2FBF71' : est.estado === 'en_firma' ? '#f59e0b' : '#94a3b8';
                return (
                  <div key={ex.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', padding: '10px 0', borderTop: '1px solid rgba(51,65,85,0.6)' }}>
                    <div style={{ minWidth: 0, flex: '1 1 200px' }}>
                      <div style={{ color: '#e2e8f0', fontSize: '0.86rem', fontWeight: 600 }}>
                        {ETIQUETA_TIPO_EXAMEN[ex.exam_type ?? ''] ?? 'Examen'} · {fecha(ex.exam_date)}
                      </div>
                      <div style={{ color: '#64748b', fontSize: '0.74rem' }}>
                        {ETIQUETA_APTITUD[ex.aptitude ?? ''] ?? 'Sin aptitud'}{ex.physician ? ` · ${ex.physician}` : ''}
                      </div>
                    </div>
                    <button type="button" onClick={() => setAbierto(ex)}
                      style={{ padding: '8px 12px', minHeight: 40, background: 'transparent', border: `1px solid ${color}66`, color, borderRadius: 8, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
                      {est.estado === 'firmado' ? '✓ Firmado' : est.estado === 'en_firma' ? est.texto : 'Firmar certificado'}
                    </button>
                  </div>
                );
              })}
        </div>
      </div>
    </div>
  );
}
